import { CheckCircleIcon, InfoIcon, XCircleIcon, XIcon } from '@phosphor-icons/react';
import toast, { type Toast as ToastType, resolveValue } from 'react-hot-toast';

import classNames from '@/utils/classnames';

import { Spinner } from './Spinner';

type Variant = {
  bg: string;
  border: string;
  icon: React.ReactNode;
};

const variantFor = (t: ToastType): Variant => {
  switch (t.type) {
    case 'success':
      return {
        bg: 'bg-success-muted',
        border: 'border-success/20',
        icon: <CheckCircleIcon size={18} weight="fill" className="text-success mt-0.5 shrink-0" />,
      };
    case 'error':
      return {
        bg: 'bg-error-muted',
        border: 'border-error/20',
        icon: <XCircleIcon size={18} weight="fill" className="text-error mt-0.5 shrink-0" />,
      };
    case 'loading':
      return {
        bg: 'bg-surface-2',
        border: 'border-line',
        icon: <Spinner size={4} className="text-fg-tertiary mt-0.5 shrink-0" />,
      };
    default:
      return {
        bg: 'bg-surface-2',
        border: 'border-line',
        icon: <InfoIcon size={18} weight="fill" className="text-info mt-0.5 shrink-0" />,
      };
  }
};

interface ToastProps {
  t: ToastType;
}

export const Toast = ({ t }: ToastProps) => {
  const v = variantFor(t);

  return (
    <div
      className={classNames(
        'flex items-start gap-3 rounded-xl border px-3.5 py-3',
        'min-w-70 max-w-105 shadow-tremor-dropdown',
        'transition-opacity duration-150',
        v.bg,
        v.border,
        t.visible ? 'opacity-100' : 'opacity-0',
      )}
      role={t.type === 'error' ? 'alert' : 'status'}
      aria-live={t.type === 'error' ? 'assertive' : 'polite'}
    >
      {v.icon}

      <div className="flex-1 text-xs leading-relaxed text-fg-secondary">{resolveValue(t.message, t)}</div>

      <button
        type="button"
        onClick={() => toast.dismiss(t.id)}
        aria-label="Dismiss notification"
        className={classNames(
          'shrink-0 -mr-1 -mt-0.5 inline-flex size-6 items-center justify-center',
          'rounded-md text-fg-tertiary hover:text-fg hover:bg-black/5',
          'dark:hover:bg-white/5 transition-colors duration-150',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        )}
      >
        <XIcon size={14} weight="bold" />
      </button>
    </div>
  );
};
